import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { clamp, floor } from "lodash";
import styled, { css } from "styled-components";
import { Icon, theme, useHolder } from "uikit";

import Style, {
	baseStyles,
	distributeStyles,
	flexStyles,
	typographyStyles,
	visible,
	StyledGrid,
} from "../styles";

interface StyledProps {
	disabled: boolean;
}

const StyledStepper = styled(StyledGrid)<StyledProps>`
	box-sizing: border-box;
	height: 32px;
	display: grid;
	grid-template-columns: 1fr 18px;
	overflow: hidden;

	background-color: white;
	border: 1px solid ${theme.colors.color_border_basic};
	border-radius: 5px;

	.step-buttons {
		display: grid;
		grid-template-columns: 1fr;
		grid-template-rows: 1fr 1fr;
		justify-content: center;
		align-items: center;

		border-left: 1px solid;
		border-color: ${theme.colors.color_border_basic};

		.step-button {
			width: 100%;
			height: 100%;
			display: flex;
			justify-content: center;
			align-items: center;
			padding: 0;
			background-color: transparent;
			border: none;
			cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

			& svg {
				fill: ${theme.colors.text_placeholder};
			}

			&:hover svg {
				fill: ${theme.colors.primary};
			}
		}
	}

	&[data-disabled="true"] {
		background: ${theme.colors.disabled_filled};
		border-color: ${theme.colors.color_border_basic};

		.input {
			color: ${theme.colors.disabled_text};
		}

		.step-buttons {
			border-color: ${theme.colors.color_border_basic};

			.step-button svg {
				fill: ${theme.colors.disabled_text};
			}
		}
	}

	&:has(.input:focus) {
		border-color: ${theme.colors.accent};

		.input,
		.step-buttons {
			border-color: ${theme.colors.accent};
		}
	}
`;

const StyledInput = styled.input<NegativeValueStepperBase.StyleInput>`
	font-family: ${theme.font.l};
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 18px;
	text-align: center;

	width: 100%;
	border: none;
	outline: none;
	padding: 0;

	&:hover {
		border-color: ${theme.colors.primary};
	}

	&::-webkit-inner-spin-button {
		display: none;
	}

	${({ disabled }) =>
		disabled &&
		css`
			filter: grayscale(1);
			opacity: 0.3;
			cursor: not-allowed;
		`}
	${baseStyles}
	${flexStyles}
	${distributeStyles}
	${typographyStyles}
	${visible}
`;

const createDigitPattern = (decimalCount: number) => {
	if (decimalCount > 0) {
		return new RegExp(`^\\d*(?:[.,]\\d{0,${decimalCount}})?$`);
	}

	return /^\d*$/;
};

const normalizeNonPositiveValue = (value: number | undefined): number => {
	if (value == null || Number.isNaN(value) || value === 0) return 0;

	return value > 0 ? -Math.abs(value) : value;
};

const formatDigits = (
	value: number,
	decimalCount: number,
	delimiter = ",",
): string => {
	const abs = Math.abs(normalizeNonPositiveValue(value));

	if (abs === 0) {
		return decimalCount > 0
			? `0${delimiter}${"0".repeat(decimalCount)}`
			: "0";
	}

	return abs.toFixed(decimalCount).replace(".", delimiter);
};

const parseDigits = (digits: string, decimalCount: number): number => {
	if (!digits) return 0;

	const normalized = digits.replace(",", ".");
	const parsed = Number(normalized);

	if (Number.isNaN(parsed)) return 0;

	if (parsed === 0) return 0;

	return -clamp(floor(parsed, decimalCount), 0, Infinity);
};

const isZeroInput = (digits: string): boolean => {
	if (!digits.trim()) return true;

	const normalized = digits.replace(",", ".");
	const parsed = Number(normalized);

	return !Number.isNaN(parsed) && parsed === 0;
};

const toDisplayValue = (
	digits: string,
	decimalCount: number,
	delimiter = ",",
): string => {
	if (!digits.trim()) {
		return formatDigits(0, decimalCount, delimiter);
	}

	if (isZeroInput(digits)) {
		return digits;
	}

	return `-${digits}`;
};

const NegativeValueStepperBase: React.FC<NegativeValueStepperBase.Props> = ({
	id,
	disabled = false,
	value = 0,
	onChange,
	step = 1,
	decimalCount = 2,
	delimiter = ",",
	width = "90px",
	getRef,
	inputStyle,
	...props
}) => {
	const ref = useRef<HTMLInputElement | null>(null);
	const [inFocus, setInFocus] = useState(false);
	const [digits, setDigits] = useState(() =>
		formatDigits(value, decimalCount, delimiter),
	);

	const digitPattern = createDigitPattern(decimalCount);
	const maxValue = 0;
	const minValue = -Infinity;

	useEffect(() => {
		if (!inFocus) {
			setDigits(formatDigits(value, decimalCount, delimiter));
		}
	}, [decimalCount, delimiter, inFocus, value]);

	const emitValue = useCallback(
		(nextDigits: string) => {
			const processed = clamp(
				parseDigits(nextDigits, decimalCount),
				minValue,
				maxValue,
			);

			onChange?.(processed);
		},
		[decimalCount, onChange],
	);

	const setDigitsValue = useCallback(
		(nextDigits: string) => {
			setDigits(nextDigits);
			emitValue(nextDigits);
		},
		[emitValue],
	);

	const increase = useCallback(() => {
		const current = clamp(
			parseDigits(digits, decimalCount),
			minValue,
			maxValue,
		);
		const next = clamp(current + step, minValue, maxValue);
		const nextDigits = formatDigits(next, decimalCount, delimiter);

		setDigits(nextDigits);
		onChange?.(next);
	}, [decimalCount, delimiter, digits, onChange, step]);

	const decrease = useCallback(() => {
		const current = clamp(
			parseDigits(digits, decimalCount),
			minValue,
			maxValue,
		);
		const next = clamp(current - step, minValue, maxValue);
		const nextDigits = formatDigits(next, decimalCount, delimiter);

		setDigits(nextDigits);
		onChange?.(next);
	}, [decimalCount, delimiter, digits, onChange, step]);

	const increaseHolder = useHolder(350, 50, increase);
	const decreaseHolder = useHolder(350, 50, decrease);

	const onChangeInput = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let raw = event.target.value;

			if (raw.startsWith("-")) {
				raw = raw.slice(1);
			}

			if (!digitPattern.test(raw)) return;

			setDigitsValue(raw);
		},
		[digitPattern, setDigitsValue],
	);

	const onKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			const { key } = event;

			if (key === "ArrowUp") {
				event.preventDefault();
				increase();
				return;
			}

			if (key === "ArrowDown") {
				event.preventDefault();
				decrease();
				return;
			}

			if (key === "-" || key === "+") {
				event.preventDefault();
			}
		},
		[decrease, increase],
	);

	const shareRef = useCallback(
		(input: HTMLInputElement | null) => {
			ref.current = input;
			getRef?.(input);
		},
		[getRef],
	);

	const displayValue = toDisplayValue(digits, decimalCount, delimiter);

	return (
		<StyledStepper
			areas=""
			data-disabled={disabled}
			disabled={disabled}
			style={{ width }}
			onClick={(event) => {
				event.stopPropagation();
			}}
			{...props}
		>
			<StyledInput
				ref={shareRef}
				id={id}
				type="text"
				className="input"
				inputMode="decimal"
				value={displayValue}
				disabled={disabled}
				onChange={onChangeInput}
				onKeyDown={onKeyDown}
				onFocus={(event) => {
					setInFocus(true);
					event.target.select();
				}}
				onBlur={() => {
					setInFocus(false);
					setDigits(formatDigits(value, decimalCount, delimiter));
				}}
				{...inputStyle}
			/>
			<div className="step-buttons">
				<button
					type="button"
					className="step-button"
					tabIndex={-1}
					disabled={disabled}
					{...increaseHolder.events}
				>
					<Icon
						id="stepper-arrow-up"
						size={12}
						colors={[theme.colors.primary]}
					/>
				</button>

				<button
					type="button"
					className="step-button"
					tabIndex={-1}
					disabled={disabled}
					{...decreaseHolder.events}
				>
					<Icon
						id="stepper-arrow-down"
						size={12}
						colors={[theme.colors.primary]}
					/>
				</button>
			</div>
		</StyledStepper>
	);
};

declare namespace NegativeValueStepperBase {
	type Value = number | undefined;
	type StyleInput = Style.BaseType &
		Style.FlexType &
		Style.DistributeType &
		Style.TypographyType;

	interface Props extends Partial<Style.Grid> {
		id?: string;
		disabled?: boolean;
		value?: number;
		onChange?: (value: number) => void;
		step?: number;
		decimalCount?: number;
		delimiter?: string;
		width?: string;
		getRef?: (ref: HTMLInputElement | null) => void;
		inputStyle?: StyleInput;
	}
}

export const NegativeValueStepper = memo(NegativeValueStepperBase);

declare namespace NegativeValueStepper {
	interface Props extends NegativeValueStepperBase.Props {}
}

export default NegativeValueStepperBase;
